import React, {useEffect, useMemo, useState} from 'react';
import {debounce} from 'lodash';

import {Modal} from '@deviceinsight/ng-ui-basic-components';
import {Reactable, ReactableColumnDef, ReactableFilterSettings} from '@deviceinsight/ng-ui-components';
import {Pageable, PaginationSettings} from '@deviceinsight/ng-ui-api-client';

import {Trans} from '@deviceinsight/ng-ui-scale-lib/api';
import makeCancelable from '../../utils/makeCancelable';
import {DATAPOINT_TYPE_LABELS, DpTypeIcon} from './datapoints/DpTypeIcon';
import {DataProcessingDefinition, listDataProcessingDefinitions} from '../../api/dataProcessingDefinitionApi';

const initialPaginationSettings: PaginationSettings = {
	page: 0,
	size: 5,
	sort: {
		property: 'kpiName',
		direction: 'ASC'
	}
};

type Props = {
	onSelectDataProcessingDefinition: (arg0: DataProcessingDefinition) => any;
	onOutsideClick: () => void;
};

export default function SingleDataProcessingDefinitionSelectionTableModal(props: Props) {
	const {onSelectDataProcessingDefinition, onOutsideClick} = props;
	const [filterSettings, setFilterSettings] = useState<ReactableFilterSettings>({});
	const [loading, setLoading] = useState<boolean>(true);
	const [dataProcessingDefinitions, setDataProcessingDefinitions] = useState<
		Pageable<DataProcessingDefinition> | null | undefined
	>();
	const [paginationSettings, setPaginationSettings] = useState<PaginationSettings>(initialPaginationSettings);

	function handleSelectDataProcessingDefinition(selectedDefinition: DataProcessingDefinition) {
		setLoading(true);
		onSelectDataProcessingDefinition(selectedDefinition);
	}

	function handleChangePage(page: number) {
		setPaginationSettings({...paginationSettings, page});
	}

	function handleSort(property: string, direction: 'ASC' | 'DESC') {
		setPaginationSettings({
			...paginationSettings,
			sort: {
				property,
				direction
			}
		});
	}

	const handleFilter = useMemo(
		() =>
			debounce((filterSettings: ReactableFilterSettings) => {
				setFilterSettings(filterSettings);
			}, 500),
		[setFilterSettings]
	);
	useEffect(() => {
		setLoading(true);
		const filterSettingsForBackend = {
			kpiName: filterSettings.kpiName,
			publishAsDatapointValues: filterSettings.datapointType
		};
		const {promise, cancel} = makeCancelable(
			listDataProcessingDefinitions(filterSettingsForBackend, paginationSettings)
		);
		promise
			.then((definitions) => {
				setDataProcessingDefinitions(definitions);
				setLoading(false);
			})
			.catch((e) => console.warn("Couldn't fetch data processing definitions for single selection", e));
		return cancel;
	}, [paginationSettings, filterSettings]);
	const columnDefs = getColumnDefs(handleSelectDataProcessingDefinition);
	return (
		<Modal
			content={
				<div>
					<h2>
						<Trans i18nKey="rules.singleDataProcessingDefinitionSelectionTableTitle">
							Please select a Definition...
						</Trans>
					</h2>
					<Reactable
						columnDefs={columnDefs}
						isLoading={loading}
						pageable={dataProcessingDefinitions}
						uniqueItemKey={(item) => item.id}
						paginationSettings={paginationSettings}
						onChangePage={handleChangePage}
						onSort={handleSort}
						onFilter={handleFilter}
					/>
				</div>
			}
			width={800}
			onOutsideClick={onOutsideClick}
		/>
	);
}

export function getColumnDefs(
	onSelectDataProcessingDefinition: (arg0: DataProcessingDefinition) => any
): Array<ReactableColumnDef<DataProcessingDefinition>> {
	const columnDefs: Array<ReactableColumnDef<DataProcessingDefinition>> = [
		{
			name: 'kpiName',
			label: <Trans i18nKey="globalDataProcessingDefinitions.definitionName">Definition Name</Trans>,
			sortable: true,
			filterable: {
				filterType: 'text'
			},
			render: (item: DataProcessingDefinition) => item.definition.kpiName
		},
		{
			name: 'datapointType',
			label: <Trans i18nKey="globalDataProcessingDefinitions.datapointType">Type</Trans>,
			width: '5%',
			filterable: {
				filterType: 'options',
				filterOptions: [
					{
						value: 'false',
						label: {
							id: 'general.publishedAs.kpi',
							defaultValue: 'KPI'
						}
					},
					{
						value: 'true',
						label: {
							id: 'general.dpType.calculated',
							defaultValue: 'Calculated Datapoint'
						}
					}
				]
			},
			render: ({definition: {publishAsDatapointValues}}: DataProcessingDefinition) => {
				const publishedType = publishAsDatapointValues ? DATAPOINT_TYPE_LABELS[0] : DATAPOINT_TYPE_LABELS[1];
				return (
					<span>
						<DpTypeIcon dpType={publishedType.value} />
						{typeof publishedType.label === 'string' ? (
							publishedType.label
						) : (
							<Trans i18nKey={publishedType.label.id}>{publishedType.label.defaultValue}</Trans>
						)}
					</span>
				);
			}
		},
		{
			name: 'numberOfDatapoints',
			label: <Trans i18nKey="globalDataProcessingDefinitions.numberOfDatapoints">DPs</Trans>,
			width: '5%',
			sortable: false,
			render: (item: DataProcessingDefinition) => item.definition.input.length
		}
	];
	return columnDefs.map((columnDef) => {
		return {...columnDef, onClick: onSelectDataProcessingDefinition};
	});
}
